import { type IReactionDisposer, makeAutoObservable, reaction } from "mobx";
import { apiClient } from "../../common/apiClient";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { ComplianceDocument } from "../../common/types/complianceDocumentTypes";
import {
   type AsyncData,
   initializeWithDefaultData,
   isAbortError,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../../common/utils/asyncDataUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";

type ComplianceDocumentStore = BaseStoreType & {
   documents: AsyncData<ComplianceDocument[]>;

   init: () => IReactionDisposer | undefined;
   fetchDocuments: (accessToken?: string | null) => Promise<void>;
   downloadDocument: (documentId: string, accessToken?: string | null) => Promise<Blob>;
};

const complianceDocumentStore: ComplianceDocumentStore = makeAutoObservable({
   documents: initializeWithDefaultData<ComplianceDocument[]>([]),

   init: () => {
      if (theme.storeId !== "tinehandel") {
         return;
      }

      return reaction(
         () => authStore.isLoggedIn(),
         (isLoggedIn) => {
            const accessToken = isLoggedIn ? authStore.getSessionToken() : null;
            void complianceDocumentStore.fetchDocuments(accessToken);
         },
         { fireImmediately: true }
      );
   },

   fetchDocuments: async (accessToken = null) => {
      const url = resolvePlatformRequestUrl(platformRoutes.complianceDocuments, { storeId: theme.storeId });
      const controller = setAsWaitingForData(complianceDocumentStore.documents);
      const isCurrentRequest = () => complianceDocumentStore.documents.abortController === controller;

      try {
         const documents: ComplianceDocument[] = await apiClient(url, accessToken, controller).get().json();
         if (!isCurrentRequest()) {
            return;
         }
         setAsDataAvailable(complianceDocumentStore.documents, documents);
      } catch (error) {
         if (isAbortError(error) || !isCurrentRequest()) {
            return;
         }
         setAsErrorOccured(complianceDocumentStore.documents, `${error}`);
      }
   },

   downloadDocument: (documentId, accessToken = null) => {
      const url = resolvePlatformRequestUrl(platformRoutes.complianceDocumentContent, {
         storeId: theme.storeId,
         documentId
      });
      return apiClient(url, accessToken, undefined, { headers: { Accept: "application/pdf" } })
         .get()
         .blob();
   },

   clearCompanySpecificData: () => {
      complianceDocumentStore.documents.abortController?.abort();
      complianceDocumentStore.documents = initializeWithDefaultData([]);
   }
} satisfies ComplianceDocumentStore);

export default complianceDocumentStore;
